import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchParishBySlug } from '@/services/api';

interface Parish {
  id: number;
  name: string;
  slug: string;
  location?: string;
  photo?: string;
  archdeaconry?: {
    id: number;
    name: string;
    slug: string;
  };
  priests?: {
    id: number;
    title: string;
    name: string;
    slug: string;
    photo?: string;
  }[];
}

const ParishProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [parish, setParish] = useState<Parish | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadParish = async () => {
      try {
        const response = await fetchParishBySlug(slug!);
        setParish(response.data);
      } catch (error) {
        console.error(error);
        toast({
          title: 'Error',
          description: 'Failed to fetch parish details.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadParish();
  }, [slug, toast]);

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-500 text-lg animate-pulse">
        Loading parish information...
      </div>
    );
  }

  if (!parish) {
    return (
      <div className="p-10 text-center text-red-500 text-lg">
        Parish not found.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <Card className="shadow-md border rounded-2xl overflow-hidden">
        <div className="md:flex">
          {/* Parish Photo */}
          <div className="md:w-1/3 bg-gray-100 flex items-center justify-center p-6">
            {parish.photo ? (
              <img
                src={parish.photo}
                alt={parish.name}
                className="w-full h-auto rounded-lg object-cover"
              />
            ) : (
              <div className="text-gray-400 italic">No parish photo</div>
            )}
          </div>

          {/* Parish Details */}
          <div className="md:w-2/3 p-6 space-y-4">
            <CardHeader className="p-0 mb-3">
              <CardTitle className="text-3xl font-semibold text-gray-800">
                {parish.name}
              </CardTitle>
              {parish.location && (
                <p className="text-sm text-gray-500">{parish.location}</p>
              )}
            </CardHeader>

            <CardContent className="p-0 space-y-3 text-gray-700 text-[15px]">
              {parish.archdeaconry && (
                <p>
                  <strong>Archdeaconry:</strong>{' '}
                  <Link
                    to={`/archdeaconries/${parish.archdeaconry.slug}`}
                    className="text-blue-600 hover:underline"
                  >
                    {parish.archdeaconry.name}
                  </Link>
                </p>
              )}

              <div>
                <strong>Clergy Members:</strong>
                {parish.priests && parish.priests.length > 0 ? (
                  <ul className="mt-2 space-y-3">
                    {parish.priests.map((clergy) => (
                      <li
                        key={clergy.id}
                        className="flex items-center gap-4 border-b pb-2"
                      >
                        {clergy.photo ? (
                          <img
                            src={clergy.photo}
                            alt={clergy.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm text-white">
                            {clergy.name?.charAt(0)}
                          </div>
                        )}
                        <Link to={`/clergy/${clergy.slug}`}
                          className="text-blue-700 hover:underline font-medium"
                        >
                          {clergy.title} {clergy.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 mt-1">No clergy assigned yet.</p>
                )}
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ParishProfile;
